import { useEffect } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Avatar } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { NAV, SECTION_ORDER, navItemForPath, type PlatformRole } from '@/components/platform/nav'
import { getMe } from '@/services/api'

function crumbsForPath(pathname: string): string[] {
  const current = navItemForPath(pathname)
  if (!current) return ['Platform']
  return [current.section, current.label].filter(Boolean) as string[]
}

function initialsFromName(name: string): string {
  return name.split(' ').map((part) => part[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || 'BI'
}

export function PlatformLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const crumbs = crumbsForPath(location.pathname)

  useEffect(() => {
    const token = localStorage.getItem('platform_jwt')
    if (!token) navigate('/login', { replace: true })
  }, [navigate])

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false })

  const role = (me?.role as PlatformRole) || 'owner'
  const userName = me?.name || [me?.first_name, me?.last_name].filter(Boolean).join(' ') || me?.email || 'Equipo Bisne'
  const initials = initialsFromName(userName)

  const handleLogout = () => {
    localStorage.removeItem('platform_jwt')
    navigate('/login', { replace: true })
  }

  const visible = NAV.filter((item) => item.roles.includes(role))
  const sections = visible.reduce<Record<string, typeof visible>>((acc, item) => {
    const key = item.section || '_'
    acc[key] = acc[key] || []
    acc[key].push(item)
    return acc
  }, {})

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-top">
          <NavLink to="/dashboard" className="sidebar-brand">
            <span>bisne</span>
            <span className="brand-dot" />
            <small>platform</small>
          </NavLink>
        </div>
        <nav className="sidebar-nav">
          {SECTION_ORDER.map((section) => {
            const items = sections[section]
            if (!items?.length) return null
            return (
              <div key={section} className="sidebar-section">
                {section !== '_' ? <div className="sidebar-section-title">{section}</div> : null}
                {items.map((item) => (
                  <NavLink key={item.id} to={item.href} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                    <Icon name={item.icon} size={16} />
                    <span>{item.label}</span>
                    {typeof item.badge === 'number' && item.badge > 0 ? <span className="badge-count">{item.badge}</span> : null}
                  </NavLink>
                ))}
              </div>
            )
          })}
        </nav>
        <div className="sidebar-bottom">
          <Avatar initials={initials} />
          <span className="meta">
            <span className="name">{userName}</span>
            <span className="role">{role}</span>
          </span>
          <button type="button" className="topbar-icon-btn" onClick={handleLogout} aria-label="Cerrar sesión" style={{ width: 'auto', height: 'auto' }}>
            <Icon name="logOut" size={14} style={{ opacity: 0.5 }} />
          </button>
        </div>
      </aside>
      <div className="main">
        <div className="topbar">
          <div className="breadcrumb">
            {crumbs.map((crumb, index) => (
              <span key={crumb} className={index === crumbs.length - 1 ? 'current' : ''}>
                {index > 0 ? <span className="sep">/ </span> : null}{crumb}
              </span>
            ))}
          </div>
          <div className="topbar-spacer" />
          <div className="search">
            <Icon name="search" size={14} />
            <input aria-label="Buscar" placeholder="Buscar tenants, accounts, invoices…" readOnly />
            <kbd>⌘K</kbd>
          </div>
          <button className="topbar-icon-btn" type="button" aria-label="Notificaciones">
            <Icon name="bell" size={16} />
          </button>
          <span style={{ width: 1, height: 24, background: 'var(--border)', margin: '0 4px' }} />
          <Avatar initials={initials} size="sm" />
        </div>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
